/**
 * Shared dnd-kit plumbing for a single-axis, vertical sortable list.
 *
 * Owns the two pieces every sortable list in the app configured identically:
 *   - the sensor pair (pointer + keyboard with `sortableKeyboardCoordinates`)
 *   - a `handleDragEnd` factory that resolves the drag event to
 *     `{ oldIndex, newIndex }` against the caller's id list.
 *
 * It deliberately stops at resolving the indices — the caller applies the move
 * however its data layer requires (react-hook-form `move()`, `arrayMove()`, a
 * store action, …). That data-layer-agnostic seam is what lets lists share the
 * sensor + drag-end plumbing without a shared row body.
 *
 * Pair with `<DragHandle />` (`@/shared/ui`) for the grip button + a11y
 * focus-ring, and drive the `DndContext` with `sensors` + `onDragEnd`.
 */
import { useMemo } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

export interface SortableReorder {
  oldIndex: number;
  newIndex: number;
}

export interface UseSortableListResult {
  /** Pass straight to `<DndContext sensors={...}>`. */
  sensors: ReturnType<typeof useSensors>;
  /**
   * `onDragEnd` handler. Resolves the active/over ids to their positions in
   * `ids` and invokes `onReorder` — unless the drop was a no-op (no `over`
   * target, dropped on itself, or either id missing from `ids`).
   */
  handleDragEnd: (event: DragEndEvent) => void;
}

/**
 * @param ids   Ordered list of the sortable item ids, in the same order the
 *              rows render. Must match the `SortableContext items` prop.
 * @param onReorder Called with the resolved `{ oldIndex, newIndex }` when a
 *              drag lands on a new position.
 */
export function useSortableList(
  ids: UniqueIdentifier[],
  onReorder: (reorder: SortableReorder) => void,
): UseSortableListResult {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useMemo(
    () => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = ids.indexOf(active.id);
      const newIndex = ids.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder({ oldIndex, newIndex });
    },
    [ids, onReorder],
  );

  return { sensors, handleDragEnd };
}
